// Decides *when* a validation message is allowed on screen. The rules
// themselves live in applicationValidation.ts.
//
// Errors used to be frozen into state at submit time, so a field the
// operator had already filled in wrong stayed silent until they pressed
// Submit, and a field they had just fixed kept its red text until a
// prune-on-fix effect caught up. Here the client errors are recomputed
// every render from current form state and gated on visibility instead:
//
//   - a field speaks up once it has been blurred, then tracks live as
//     the value changes, so fixing it clears the message immediately
//   - submitting reveals everything at once, including fields never
//     visited
//   - group rules (percent totals) have no input of their own, so they
//     surface on submit only -- the running-total indicator is the live
//     feedback for those
//   - server field errors outrank client ones and stay put until the
//     next submit, since only the server can say whether they're fixed

import { useCallback, useRef, useState, type FocusEvent } from "react";

export type FieldErrorLike = { field: string; message: string };

const NO_TOUCHED: ReadonlySet<string> = new Set<string>();

export function useFieldErrors(
  clientErrors: readonly FieldErrorLike[],
  opts?: {
    /** For forms whose element ids are namespaced (the intake form
     *  prefixes everything with "ife-"), map an id back to its rule key. */
    fieldForElementId?: (elementId: string) => string;
  },
) {
  const [touched, setTouched] = useState<ReadonlySet<string>>(NO_TOUCHED);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [serverErrors, setServerErrors] = useState<readonly FieldErrorLike[]>(
    [],
  );

  // Reading through a ref keeps the blur handler referentially stable
  // even though `clientErrors` is a fresh array on every render.
  const clientRef = useRef(clientErrors);
  clientRef.current = clientErrors;
  const toFieldRef = useRef(opts?.fieldForElementId);
  toFieldRef.current = opts?.fieldForElementId;

  // focusout bubbles, so a single handler on the <form> covers every
  // control inside it -- inputs, the SelectField button, the combobox,
  // the date input -- without an onBlur prop on each one. Input ids are
  // kept equal to validator field keys for exactly this reason (that's
  // also what scroll-to-first-error relies on).
  const onBlurCapture = useCallback((e: FocusEvent<HTMLElement>) => {
    const elementId = (e.target as HTMLElement).id;
    if (!elementId) return;
    const field = toFieldRef.current?.(elementId) ?? elementId;
    setTouched((prev) => {
      if (prev.has(field)) return prev;
      const next = new Set(prev);
      next.add(field);
      return next;
    });
  }, []);

  const isVisible = (field: string) =>
    submitAttempted || touched.has(field);

  const fieldError = (field: string): string | undefined => {
    const fromServer = serverErrors.find((e) => e.field === field);
    if (fromServer) return fromServer.message;
    if (!isVisible(field)) return undefined;
    return clientErrors.find((e) => e.field === field)?.message;
  };

  // What the roll-up banner lists. Before the first submit it stays empty
  // even if a blurred field is showing inline red, so a single skipped
  // field doesn't summon a summary of the whole form.
  const visibleErrors: FieldErrorLike[] = [
    ...serverErrors,
    ...(submitAttempted
      ? clientErrors.filter(
          (e) => !serverErrors.some((s) => s.field === e.field),
        )
      : []),
  ];

  /** Call at the top of a submit handler: reveals every rule and drops
   *  the previous round of server errors. Returns the current client
   *  errors so the caller can bail before hitting the network. */
  const beginSubmit = useCallback((): readonly FieldErrorLike[] => {
    setSubmitAttempted(true);
    setServerErrors([]);
    return clientRef.current;
  }, []);

  /** Back to a pristine form (mode switches, successful save). */
  const resetErrorDisplay = useCallback(() => {
    setSubmitAttempted(false);
    setServerErrors([]);
    setTouched(NO_TOUCHED);
  }, []);

  return {
    fieldError,
    visibleErrors,
    onBlurCapture,
    beginSubmit,
    setServerErrors,
    resetErrorDisplay,
    submitAttempted,
  };
}
